import React from "react";

import NumericInput from "./NumericInput";
import DimensionUtils from "../DimensionUtils";
import { resolveUnitConfig, unitNamesOf } from "./unitConfig";
import { TFieldAccess } from "../../parametersProcessor/paramAccess";
import { withCrossFieldBounds, TParamRef } from "../../parametersProcessor/paramBounds";
import { ParametersContext } from "../../parametersProcessor/ParametersProcessor";
import IUnitsTypeDescriptor from "../../database/interfaces/paramScript/IUnitsTypeDescriptor";
import { IRelationFieldOverride } from "../../database/interfaces/paramScript/IParamTypeDescriptor";

/**
 * EGY-EGYSÉGES NUMERIKUS MEZŐ — "600mm"-alakú stringet olvas és ugyanilyet ad vissza.
 *
 * Ez a réteg korábban ÖT helyen élt külön: `SizeCardContent` (inline), `CompositeCardContent`
 * (`CompositeSizeField`), `DistributionCardContent` (`DistributionItem`), `BackPassingCardContent` és
 * `RailingCardContent` (mindkettő `UnitValueInput` néven). A `NumericInput` 14 propja mind az öt
 * híváshelyen ugyanúgy állt elő, a `resolveUnitConfig`-ból származó hét érték (min, max, lépések,
 * tizedesek, egység, `valueEditable`, `valueLabel`) pedig kézzel volt szétterítve — összesen 35
 * propátadás. Itt a `cfg` a mezőn BELÜL keletkezik, tehát ezek eltűnnek.
 *
 * A négy KIEGÉSZÍTŐ szabály opcionális prop, mert híváshelyenként más:
 *   - `override`    — kötés-függő numerikus felülírás (BackPassing `byRelation`); ugyanaz az alak,
 *                     amit a `guardDimension` és a `TFieldSpec` is használ,
 *   - `bounds`      — kereszt-mező korlátok (`minFrom`/`maxFrom`), a `ParametersContext`-ből feloldva,
 *   - `unitOptions` — szűkített egység-készlet (az elosztás "legalább egy arányos" szabálya); a
 *                     `NumericInput` egy elemű listánál felirattá váltja a választót,
 *   - `unitChange`  — lásd lentebb.
 *
 * A `unitChange` SZÁNDÉKOSAN KÖTELEZŐ, nem alapértelmezett: ma kétféle szemantika él egymás mellett
 * (a Size és az elosztás az új egység descriptor-defaultjára ugrik, a Composite / BackPassing / Railing
 * megtartja a számot). Ezek egységesítése KÜLÖN feladat — addig minden híváshelynek ki kell mondania a
 * sajátját, hogy a viselkedése bitre a mai maradjon. Kötelező propnál ez nem felejthető el.
 */
interface IUnitFieldProps {
    label?: string;
    tooltip?: string;
    placeholder?: string;
    /** Tárolási alakú érték ("600mm"); ugyanilyet ad vissza az `onChange`. */
    valueStr: string;
    units: IUnitsTypeDescriptor | undefined;
    /** Szűkített egység-készlet. Hiányában a descriptor összes egysége. */
    unitOptions?: string[];
    /** Kötés-függő numerikus felülírás (BackPassing `byRelation[kötés]`). */
    override?: IRelationFieldOverride;
    /** Kereszt-mező korlátok — a hivatkozott paraméterek a `ParametersContext`-ből oldódnak fel. */
    bounds?: { minFrom?: TParamRef; maxFrom?: TParamRef };
    access: TFieldAccess;
    /** `"default"`: az új egység descriptor-defaultja; `"keep"`: a szám megmarad. */
    unitChange: "default" | "keep";
    onChange: (value: string) => void;
}

const UnitField: React.FC<IUnitFieldProps> = ({
    label,
    tooltip,
    placeholder,
    valueStr,
    units,
    unitOptions,
    override,
    bounds,
    access,
    unitChange,
    onChange,
}) => {
    const paramsContext = React.useContext(ParametersContext);

    // A numerikus konfiguráció EGY objektumban: descriptor → kereszt-mező korlát → kötés-függő
    // felülírás. A két szűkítés a gyakorlatban sosem esik egybe (a kereszt-korlát a Size/composite
    // méret-mezőké, a felülírás a BackPassingé), a sorrend tehát nem visel viselkedést — de a
    // felülírás áll utoljára, mert az a legspecifikusabb szabály.
    const cfg = React.useMemo(() => {
        const base = resolveUnitConfig(units, valueStr);
        const bounded = bounds ? withCrossFieldBounds(base, paramsContext.parameters, bounds) : base;
        if (!override) return bounded;

        return {
            ...bounded,
            min: override.min ?? bounded.min,
            max: override.max ?? bounded.max,
            smallStep: override.smallStep ?? bounded.smallStep,
            largeStep: override.largeStep ?? bounded.largeStep,
            decimals: override.decimals ?? bounded.decimals,
        };
    }, [units, valueStr, bounds, override, paramsContext.parameters]);

    return (
        <NumericInput
            label={label ?? ""}
            value={cfg.num}
            min={cfg.min}
            max={cfg.max}
            smallStep={cfg.smallStep}
            largeStep={cfg.largeStep}
            decimals={cfg.decimals}
            tooltip={tooltip}
            placeholder={placeholder}
            editable={access.editable}
            readOnly={access.readOnly}
            valueEditable={!cfg.isFixed}
            valueText={cfg.valueLabel}
            unitLabel={cfg.unit}
            unitOptions={unitOptions ?? unitNamesOf(units)}
            onValueChanged={(num) => onChange(DimensionUtils.formatDimension(num, cfg.unit))}
            onUnitChanged={(unit) => onChange(DimensionUtils.formatDimension(
                unitChange === "default" ? (units?.[unit]?.default ?? cfg.num) : cfg.num,
                unit
            ))}
        />
    );
};

export default UnitField;
